/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import SaveSpaceButton from "../components/SaveSpaceButton";
import HoursCheckInModal from "../components/HoursCheckInModal";
import { Toaster } from "sonner";

// Beta punch-list P0-2 · Consistent Member Perk block. Reads perk_* fields
// off the DB-backed café doc and renders "Member Perk — {perk_label}". If
// perk_terms is provided, shows a "View terms" reveal. No hard-coded perk
// values live in this component; everything comes from `db.hours_cafes`.
function PerkBlock({ cafe }) {
  const [open, setOpen] = useState(false);
  const label = (cafe.perk_label || "").trim();
  if (!label) return null;
  const terms = (cafe.perk_terms || "").trim();
  const slug = (cafe.name || "").replace(/\s+/g, '-').toLowerCase();
  return (
    <div
      data-testid={`hours-perk-${slug}`}
      style={{
        marginTop: 10,
        padding: "10px 12px",
        background: "rgba(184, 145, 58, 0.10)",
        border: "1px solid rgba(184, 145, 58, 0.28)",
        borderRadius: 8,
      }}
    >
      <div style={{fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:"var(--gold)", fontWeight:600, marginBottom:4}}>
        Member Perk
      </div>
      <div data-testid={`hours-perk-label-${slug}`} style={{fontSize:15, fontWeight:600, lineHeight:1.4, color:"var(--m-text)"}}>
        {label}
      </div>
      {terms && (
        <>
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            data-testid={`hours-perk-terms-toggle-${slug}`}
            style={{
              background:"none", border:"none", padding:0, marginTop:6,
              color:"var(--gold)", fontSize:12, cursor:"pointer", textDecoration:"underline",
            }}
          >
            {open ? "Hide terms" : "View terms"}
          </button>
          {open && (
            <div
              data-testid={`hours-perk-terms-${slug}`}
              style={{fontSize:12, marginTop:6, color:"var(--m-muted)", lineHeight:1.5}}
            >
              {terms}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HoursHeader({ user }) {
  return (
    <header className="rec-header">
      <Link to="/nh" className="rec-brand" data-testid="hours-brand">
        <span className="rec-brand-mark"><img src="/brand/metier-icon.png" alt="Métier" /></span>
      </Link>
      <nav className="rec-topnav">
        <Link to="/list-your-space">List your space</Link>
        {user ? (
          <Link to="/profile" data-testid="hours-nav-profile" title={user.name || user.email}>
            My profile →
          </Link>
        ) : (
          <Link to="/login" data-testid="hours-signin">Member sign in</Link>
        )}
      </nav>
    </header>
  );
}

// Fallback used only if /hours/regions is slow — always overwritten by API.
const FALLBACK_REGIONS = [
  { key: "all", label: "All", listing_count: 0, activation: null },
];

export default function Hours() {
  const { user } = useAuth();
  const [cafes, setCafes] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [regions, setRegions] = useState(FALLBACK_REGIONS);
  const [loading, setLoading] = useState(true);
  // Session 60.25 · Métier Hours Check-In V1
  const [checkInTarget, setCheckInTarget] = useState(null); // { cafeId, cafeName } | null

  // ?region=<key> support so region deep-links can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterKey, setFilterKey] = useState(() => (searchParams.get("region") || "all").toLowerCase());

  // Notify-me (waitlist) local state — one form, resets after submit.
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [dir, feat, regs] = await Promise.all([
          api.get("/hours/cafes"),
          api.get("/hours/featured").catch(() => ({ data: [] })),
          api.get("/hours/regions").catch(() => ({ data: FALLBACK_REGIONS })),
        ]);
        setCafes(dir.data);
        setFeatured(feat.data || []);
        setRegions(regs.data || FALLBACK_REGIONS);
      } catch {/* ignore */}
      finally { setLoading(false); }
    })();
  }, []);

  // Keep URL in sync with the chip a user clicks.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filterKey === "all") next.delete("region"); else next.set("region", filterKey);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Session 60.25 · Deep-link support for Commons / profile links pointing
  // at a specific café. `?cafe=<cafe_id>` filters to that café's region
  // and scrolls the card into view once the directory has loaded.
  useEffect(() => {
    const cafeParam = searchParams.get("cafe");
    if (!cafeParam || loading || !cafes.length) return;
    const target = cafes.find(c => c.cafe_id === cafeParam);
    if (!target) return;
    if (target.region && filterKey !== "all" && (target.region || "").toLowerCase() !== filterKey) {
      setFilterKey((target.region || "").toLowerCase());
    }
    // Give React a beat to render before scrolling.
    const t = setTimeout(() => {
      const el = document.getElementById(`cafe-${cafeParam}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cafes, searchParams]);

  const cafesForKey = useMemo(() => {
    if (filterKey === "all") return cafes;
    // Session 60.24 · Support combined-region keys (e.g. legacy `dmv` →
    // washington-dc + maryland + virginia + dmv). The backend
    // /hours/regions payload embeds an `aggregates` list on any combined
    // region so all matching logic can live in one place.
    const meta = regions.find(r => (r.key || "").toLowerCase() === filterKey);
    if (meta && Array.isArray(meta.aggregates) && meta.aggregates.length > 0) {
      const allowed = new Set(meta.aggregates.map(k => (k || "").toLowerCase()));
      return cafes.filter(c => allowed.has((c.region || "").toLowerCase()));
    }
    return cafes.filter(c => (c.region || "").toLowerCase() === filterKey);
  }, [cafes, filterKey, regions]);

  // Session 60.5 · Alphabetize A-Z with "All" pinned first. Architected as
  // a sort here (not a hard-coded ordering on the backend) so converting
  // this row of chips to a dropdown/filter later is drop-in — just swap
  // this component with a <select> that maps the same `sortedRegions`.
  //
  // Session 60.24 · Combined-market chips (e.g. legacy `dmv`) are hidden
  // from the member-facing chip bar via `member_ui_hidden: true` so we
  // surface Washington, DC / Maryland / Virginia individually. The chip
  // remains addressable via ?region=dmv for shared/legacy links.
  const sortedRegions = useMemo(() => {
    const visible = regions.filter(r => !r.member_ui_hidden);
    const all = visible.filter(r => (r.key || "").toLowerCase() === "all");
    const rest = visible
      .filter(r => (r.key || "").toLowerCase() !== "all")
      .slice()
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return [...all, ...rest];
  }, [regions]);

  const currentRegionMeta = regions.find(r => r.key === filterKey);
  const isEmptyActivation = filterKey !== "all" && cafesForKey.length === 0;

  async function submitNotify(e) {
    e.preventDefault();
    if (!notifyEmail.trim()) return;
    setNotifySending(true); setNotifyMsg(null);
    try {
      await api.post("/hours/waitlist", {
        email: notifyEmail.trim(),
        region: filterKey === "all" ? null : filterKey,
        source: "hours_notify_me",
      });
      setNotifyMsg({ ok: true, text: "You're on the list. We'll email you when Métier Hours activates." });
      setNotifyEmail("");
    } catch {
      setNotifyMsg({ ok: false, text: "Sorry, that didn't go through. Try again or email cam@workmetier.com." });
    } finally { setNotifySending(false); }
  }

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="rec-page hours-page" style={{ background: "transparent" }}>
        <main className="rec-main hours-main">
        <span className="rec-eyebrow">Métier Hours · Multi-region network</span>
        <h1 className="rec-h1">Work-friendly cafés, <em>wherever work takes you</em>.</h1>
        <p className="m-lede" style={{maxWidth:680}}>
          Discover cafés and hospitality partners offering work-friendly hours, member benefits, and welcoming places to focus, meet, and connect.
        </p>

        <div className="hours-facts">
          <div><span className="hours-fact-num">Founding</span><span>Member perks</span></div>
          <div><span className="hours-fact-num">Free</span><span>To join</span></div>
        </div>

        {/* Founding QR gate: full directory is public, but the QR itself
            requires a Métier account. Members deep-link straight to
            /profile (where the QR renders); guests get routed to /signup. */}
        <div className="hours-qr-cta" data-testid="hours-qr-cta" style={{
          marginTop: 22, padding: "18px 22px",
          background: "rgba(184, 145, 58, 0.08)",
          border: "1px solid rgba(184, 145, 58, 0.25)",
          borderRadius: 12, display: "flex", flexWrap: "wrap",
          alignItems: "center", justifyContent: "space-between", gap: 14,
        }}>
          <div style={{maxWidth: 520}}>
            <div style={{fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--gold)", fontWeight: 500, marginBottom: 4}}>
              Founding Member perk
            </div>
            <p style={{margin:0, fontSize: 15, lineHeight: 1.55, color: "var(--m-text)"}}>
              Your personal Métier Hours QR unlocks café perks, supports local small businesses, and may give you access to a workspace during quieter hours.
            </p>
          </div>
          {user ? (
            <Link to="/profile#hours-qr" className="m-btn-gold" data-testid="hours-qr-cta-view">
              View my founding QR →
            </Link>
          ) : (
            <Link to="/signup?next=/profile" className="m-btn-gold" data-testid="hours-qr-cta-signup">
              Get your founding QR →
            </Link>
          )}
        </div>

        {featured.length > 0 && (
          <section className="hours-featured" data-testid="hours-featured-section" aria-label="Featured Métier Hours partner">
            <div className="hours-featured-eyebrow">
              <span className="hours-featured-dot" aria-hidden="true">◆</span>
              Featured Métier Hours partner
            </div>
            <div className="hours-featured-grid">
              {featured.map(c => (
                <article
                  key={c.name}
                  id={c.cafe_id ? `cafe-${c.cafe_id}` : undefined}
                  className="hours-card hours-card--featured"
                  data-testid={`hours-featured-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
                >
                  <div className="hours-card-badges">
                    <span className="hours-badge hours-badge--partner" data-testid="hours-partner-badge">
                      Métier Hours Partner
                    </span>
                  </div>
                  <div className="hours-card-head">
                    <h3 className="hours-card-name">{c.name}</h3>
                    <span className="hours-card-state">{c.region === "central-pa" ? "Central PA" : c.state}</span>
                  </div>
                  {c.country && c.country.toLowerCase() !== "us" && c.country.toLowerCase() !== "united states" && (
                    <span className="hours-country-pill" data-testid={`hours-country-featured-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>{c.country}</span>
                  )}
                  <p className="hours-card-neighborhood">{c.neighborhood}</p>
                  {c.affiliate_label && (
                    <p className="hours-card-affiliate" data-testid="hours-affiliate-line">
                      {c.affiliate_label}
                    </p>
                  )}
                  <PerkBlock cafe={c} />
                  <p className="hours-card-address">
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}`} target="_blank" rel="noreferrer">
                      {c.address}
                    </a>
                  </p>
                  <p className="hours-card-hours">{c.hours}</p>
                  <div className="hours-card-tags">
                    {(c.tags || []).map(t => <span key={t} className="hours-tag">{t}</span>)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <a
                      href={c.affiliate_url || c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="m-btn-gold hours-card-cta"
                      data-testid={`hours-partner-cta-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
                    >
                      {c.cta_label || `Visit ${c.name} ↗`}
                    </a>
                    {user && c.cafe_id && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCheckInTarget({ cafeId: c.cafe_id, cafeName: c.name }); }}
                          data-testid={`hours-checkin-btn-${c.cafe_id}`}
                          className="m-btn-ghost"
                          style={{ fontSize: 13 }}
                        >
                          ✓ Check in
                        </button>
                        <SaveSpaceButton spaceType="cafe" spaceId={c.cafe_id} spaceName={c.name} />
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <div className="hours-filterbar" data-testid="hours-filters">
          {sortedRegions.map(r => (
            <button key={r.key}
              className={"hours-filter" + (filterKey === r.key ? " active" : "")}
              onClick={() => setFilterKey(r.key)}
              data-testid={`hours-filter-${r.key}`}>
              {r.label}
              {r.key !== "all" && r.listing_count > 0 && (
                <span className="hours-filter-count" aria-hidden="true"> · {r.listing_count}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="m-loading-inline">Loading cafés…</div>
        ) : isEmptyActivation ? (
          <section
            className="hours-activation"
            data-testid={`hours-activation-${filterKey}`}
            aria-label={`Activation card for ${currentRegionMeta?.label}`}
          >
            <div className="hours-activation-body">
              <span className="hours-badge hours-badge--soon">Métier Hours · Coming Soon</span>
              <h2 className="hours-activation-h">
                {currentRegionMeta?.activation || `Métier Hours is coming to ${currentRegionMeta?.label}.`}
              </h2>
              <p className="hours-activation-sub">
                Know a work-friendly café that should join the network? Nominate it for Métier Hours,
                or leave your email and we&apos;ll tell you the moment we go live.
              </p>
              <div className="hours-activation-ctas">
                <a
                  className="m-btn-gold"
                  href={`mailto:cam@workmetier.com?subject=Nominate%20a%20caf%C3%A9%20for%20M%C3%A9tier%20Hours%20%E2%80%94%20${encodeURIComponent(currentRegionMeta?.label || filterKey)}`}
                  data-testid="hours-activation-nominate"
                >
                  Nominate a café →
                </a>
              </div>
              <form className="hours-notify" onSubmit={submitNotify} data-testid="hours-notify-form">
                <label htmlFor="hours-notify-email" className="hours-notify-label">
                  Don&apos;t see your area listed? Get notified when we&apos;re live in your area.
                </label>
                <div className="hours-notify-row">
                  <input
                    id="hours-notify-email"
                    type="email"
                    required
                    placeholder="you@work.com"
                    value={notifyEmail}
                    onChange={e => setNotifyEmail(e.target.value)}
                    data-testid="hours-notify-email"
                    className="hours-notify-input"
                  />
                  <button
                    type="submit"
                    className="m-btn-ghost"
                    disabled={notifySending}
                    data-testid="hours-notify-submit"
                  >
                    {notifySending ? "Sending…" : "Notify me"}
                  </button>
                </div>
                {notifyMsg && (
                  <p
                    className={"hours-notify-msg " + (notifyMsg.ok ? "ok" : "err")}
                    data-testid="hours-notify-msg"
                  >
                    {notifyMsg.text}
                  </p>
                )}
              </form>
            </div>
          </section>
        ) : (
          <div className="hours-grid" data-testid="hours-grid">
            {cafesForKey.map(c => (
              <article
                key={c.name}
                id={c.cafe_id ? `cafe-${c.cafe_id}` : undefined}
                className="hours-card"
                data-testid={`hours-card-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
              >
                <div className="hours-card-badges">
                  <span className={
                    "hours-badge " +
                    (c.partner_status === "verified_metier_hours"
                      ? "hours-badge--partner"
                      : "hours-badge--directory")
                  }>
                    {c.partner_status === "verified_metier_hours" ? "Métier Hours Partner" : "Work-Friendly Café"}
                  </span>
                </div>
                <div className="hours-card-head">
                  <h3 className="hours-card-name">{c.name}</h3>
                  <span className="hours-card-state">{c.state === "New Hampshire" ? "NH" : c.state === "Massachusetts" ? "MA" : (c.region === "central-pa" ? "Central PA" : c.state)}</span>
                </div>
                {c.country && c.country.toLowerCase() !== "us" && c.country.toLowerCase() !== "united states" && (
                  <span className="hours-country-pill" data-testid={`hours-country-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>{c.country}</span>
                )}
                <p className="hours-card-neighborhood">{c.neighborhood}</p>
                {c.affiliate_label && (
                  <p className="hours-card-affiliate" data-testid={`hours-affiliate-line-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>
                    {c.affiliate_label}
                  </p>
                )}
                <PerkBlock cafe={c} />
                <p className="hours-card-address">
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address)}`} target="_blank" rel="noreferrer">
                    {c.address}
                  </a>
                </p>
                <p className="hours-card-hours">{c.hours}</p>
                <div className="hours-card-tags">
                  {(c.tags || []).map(t => <span key={t} className="hours-tag">{t}</span>)}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <a href={c.affiliate_url || c.url} target="_blank" rel="noreferrer" className="m-btn-ghost hours-card-cta" data-testid={`hours-visit-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>{c.cta_label || `Visit ${c.name} ↗`}</a>
                  {user && c.cafe_id && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCheckInTarget({ cafeId: c.cafe_id, cafeName: c.name }); }}
                        data-testid={`hours-checkin-btn-${c.cafe_id}`}
                        className="m-btn-ghost"
                        style={{ fontSize: 13 }}
                      >
                        ✓ Check in
                      </button>
                      <SaveSpaceButton spaceType="cafe" spaceId={c.cafe_id} spaceName={c.name} />
                    </>
                  )}
                </div>
              </article>
            ))}
            {cafesForKey.length === 0 && <div className="m-empty small">No cafés in this region yet.</div>}
          </div>
        )}

        <section className="hours-cta-section">
          <h2 className="rec-h2">Bring Métier Hours to your <em>café</em>.</h2>
          <div className="dmv-hero-ctas">
            <a href="mailto:cam@workmetier.com?subject=Bring%20M%C3%A9tier%20Hours%20to%20my%20caf%C3%A9" className="m-btn-gold" data-testid="hours-host-cta">List your café →</a>
          </div>
        </section>
      </main>
      <footer className="rec-footer">
        <span>© Métier · workmetier.com · <Link to="/llm-info" style={{color:"var(--m-muted)",textDecoration:"none"}}>For AI assistants</Link></span>
        <span>Are you a workspace owner? <Link to="/list-your-space" style={{color:"var(--gold)"}}>List your space →</Link></span>
      </footer>
    </div>
    {checkInTarget && (
      <HoursCheckInModal
        cafeId={checkInTarget.cafeId}
        cafeName={checkInTarget.cafeName}
        onClose={() => setCheckInTarget(null)}
      />
    )}
    </AppShell>
  );
}
